import { useToast } from '@cian/ui-kit';
import React, { useCallback, useState } from 'react';
import { DobroListing, TCountByStatus } from 'shared/components/DobroListing';
import { DobroListingApplicationProcessingButton } from 'shared/components/DobroListingApplicationProcessingButton';
import { DobroListingStatusPage } from 'shared/components/DobroListingStatusPage';
import { EReadyAccommodate } from 'shared/repositories/cian-dobro/entities/property/OfferV2Schema';
import { ERegion } from 'shared/types/form';
import { EOfferType, EStatus, IOffer } from 'shared/types/listing';
import { DobroListingPopupContainer } from '../DobroListingPopupContainer';
import { useFetchDobroListing } from './internal/useFetchDobroListing';

const REGION_LS_KEY = 'cian-dobro.region';
const STATUS_LS_KEY = 'cian-dobro.status';
const READY_ACCOMODATE_LS_KEY = 'cian-dobro.readyAccommodate';
const OFFER_TYPE_LS_KEY = 'cian-dobro.offerType';

function getRegionValue(): ERegion | null {
  /* istanbul ignore next */
  if (!globalThis.localStorage) {
    return null;
  }

  const allRegions: string[] = Object.values(ERegion);
  const lsValue = localStorage.getItem(REGION_LS_KEY) || '';

  return allRegions.includes(lsValue) ? (lsValue as ERegion) : null;
}

function getStatusValue(): EStatus | null {
  /* istanbul ignore next */
  if (!globalThis.localStorage) {
    return null;
  }

  const allStatues: string[] = Object.values(EStatus);
  const status = localStorage.getItem(STATUS_LS_KEY) || '';

  return allStatues.includes(status) ? (status as EStatus) : null;
}

function getReadyAccommodateValue(): EReadyAccommodate | null {
  /* istanbul ignore next */
  if (!globalThis.localStorage) {
    return null;
  }

  const allValues: string[] = Object.values(EReadyAccommodate);
  const value = localStorage.getItem(READY_ACCOMODATE_LS_KEY) || '';

  return allValues.includes(value) ? (value as EReadyAccommodate) : null;
}

function getOfferTypeValue(): EOfferType | null {
  /* istanbul ignore next */
  if (!globalThis.localStorage) {
    return null;
  }

  const allTypes: string[] = Object.values(EOfferType);
  const type = localStorage.getItem(OFFER_TYPE_LS_KEY) || '';

  return allTypes.includes(type) ? (type as EOfferType) : null;
}

export function DobroListingContainer() {
  const { result, loading, loadOffers, updateOffer } = useFetchDobroListing();
  const [regionFilter, setRegionFilter] = useState(getRegionValue);
  const [statusFilter, setStatusFilter] = useState(getStatusValue);
  const [readyAccommodateFilter, setReadyAccommodateFilter] = useState(getReadyAccommodateValue);
  const [offerTypeFilter, setOfferTypeFilter] = useState(getOfferTypeValue);
  const [modalOffer, setModalOffer] = useState<IOffer>();
  const toastApi = useToast();

  const onClose = useCallback(() => {
    setModalOffer(undefined);
  }, [setModalOffer]);

  const wrappedSetRegionFilter = useCallback(
    (region: ERegion | null) => {
      localStorage.setItem(REGION_LS_KEY, region || '');
      setRegionFilter(getRegionValue());
    },
    [setRegionFilter],
  );

  const wrappedSetStatusFilter = useCallback(
    (status: EStatus | null) => {
      localStorage.setItem(STATUS_LS_KEY, status || '');
      setStatusFilter(getStatusValue());
    },
    [setStatusFilter],
  );

  const wrappedSetReadyAccommodateFilter = useCallback(
    (status: EReadyAccommodate | null) => {
      localStorage.setItem(READY_ACCOMODATE_LS_KEY, status || '');
      setReadyAccommodateFilter(getReadyAccommodateValue());
    },
    [setReadyAccommodateFilter],
  );

  const onUpdate = useCallback(
    async (status: EStatus, comment: string) => {
      /* istanbul ignore if */
      if (!modalOffer) {
        return;
      }

      try {
        await updateOffer({
          id: modalOffer.id,
          status,
          comment,
        });

        if (status === EStatus.Canceled) {
          loadOffers();
          onClose();
        }

        toastApi.open({ type: 'success', text: 'Информация сохранена' });
      } catch (_) {
        toastApi.open({ type: 'error', text: 'Что-то пошло не так, попробуйте ещё раз' });
      }
    },
    [modalOffer, updateOffer, toastApi, loadOffers, onClose],
  );

  const applicationProcessingComponent = useCallback(
    (offerId: number, assignedFund: boolean) => {
      if (typeof result !== 'object' || result?.userMeta?.isCustomerService) {
        return null;
      }

      return (
        <DobroListingApplicationProcessingButton
          offerId={offerId}
          reloadOffers={loadOffers}
          type={assignedFund ? 'decline' : 'apply'}
        />
      );
    },
    [loadOffers, result],
  );

  if (loading) {
    return <DobroListingStatusPage title="Загрузка заявок" />;
  }

  if (result === 'no_access') {
    return (
      <DobroListingStatusPage
        title="Нет доступа"
        subtitle="Для просмотра заявок необходимо авторизоваться"
        buttonText="Авторизоваться"
        buttonLink={`/authenticate/?returnUrl=${encodeURIComponent(window.location.href)}`}
      />
    );
  }

  if (result === 'error' || !result) {
    return (
      <DobroListingStatusPage
        title="Произошла ошибка"
        subtitle="Попробуйте обновить страницу"
        buttonText="Обновить"
        buttonLink={window.location.href}
      />
    );
  }

  const offerTypeCounts = {
    all: result.offers.length,
    assignedCount: result.offers.filter(({ assignedFund }) => assignedFund).length,
    unassignedCount: result.offers.filter(({ assignedFund }) => !assignedFund).length,
  };

  let filteredOffers = result.offers;
  const anyRegionCount = filteredOffers.length;
  const fitlerByLocation = (location: string) => filteredOffers.filter(offer => offer.location === location);
  const filterByOther = () =>
    filteredOffers.filter(o => o.location !== ERegion.Moscow && o.location !== ERegion.MoscowRegion);
  const countByRegion: { [key in ERegion]: number } = {
    [ERegion.Moscow]: fitlerByLocation(ERegion.Moscow).length,
    [ERegion.MoscowRegion]: fitlerByLocation(ERegion.MoscowRegion).length,
    [ERegion.Other]: filterByOther().length,
  };

  if (regionFilter) {
    if (regionFilter === ERegion.Other) {
      filteredOffers = filterByOther();
    } else {
      filteredOffers = fitlerByLocation(regionFilter);
    }
  }

  const anyStatusCount = filteredOffers.length;
  const filterByStatus = (status: EStatus) => filteredOffers.filter(o => o.status === status);
  const countByStatus = {
    [EStatus.New]: filterByStatus(EStatus.New).length,
    [EStatus.Phone_not_available]: filterByStatus(EStatus.Phone_not_available).length,
    [EStatus.Canceled]: filterByStatus(EStatus.Canceled).length,
    [EStatus.Confirming]: filterByStatus(EStatus.Confirming).length,
    [EStatus.Busy]: filterByStatus(EStatus.Busy).length,
    [EStatus.Available]: filterByStatus(EStatus.Available).length,
  };

  if (statusFilter) {
    filteredOffers = filterByStatus(statusFilter);
  }

  const filterByReadyAccommodate = (readyAccommodate: EReadyAccommodate) =>
    filteredOffers.filter(offer => offer.readyAccommodate.includes(readyAccommodate));

  const countByReadyAccommodate = {
    all: filteredOffers.length,
    [EReadyAccommodate.During_treatment]: filterByReadyAccommodate(EReadyAccommodate.During_treatment).length,
    [EReadyAccommodate.Fire]: filterByReadyAccommodate(EReadyAccommodate.Fire).length,
    [EReadyAccommodate.Refugees]: filterByReadyAccommodate(EReadyAccommodate.Refugees).length,
    [EReadyAccommodate.River_flooding]: filterByReadyAccommodate(EReadyAccommodate.River_flooding).length,
  };

  if (readyAccommodateFilter) {
    filteredOffers = filterByReadyAccommodate(readyAccommodateFilter);
  }

  if (offerTypeFilter === EOfferType.InWork) {
    filteredOffers = filteredOffers.filter(({ assignedFund }) => assignedFund);
  }

  if (offerTypeFilter === EOfferType.FreeToApply) {
    filteredOffers = filteredOffers.filter(({ assignedFund }) => !assignedFund);
  }

  return (
    <>
      <DobroListing
        applicationProcessingComponent={applicationProcessingComponent}
        isCustomerServiceAccount={result.userMeta.isCustomerService}
        region={regionFilter}
        setRegion={wrappedSetRegionFilter}
        openModal={setModalOffer}
        offers={filteredOffers}
        status={statusFilter}
        setStatus={wrappedSetStatusFilter}
        readyAccommodate={readyAccommodateFilter}
        setReadyAccommodate={wrappedSetReadyAccommodateFilter}
        offerType={offerTypeFilter}
        setOfferType={setOfferTypeFilter}
        offerTypeCounts={offerTypeCounts}
        anyRegionCount={anyRegionCount}
        countByRegion={countByRegion}
        anyStatusCount={anyStatusCount}
        countByStatus={countByStatus as TCountByStatus}
        countByReadyAccommodate={countByReadyAccommodate}
      />
      {modalOffer && <DobroListingPopupContainer offer={modalOffer} onClose={onClose} onUpdate={onUpdate} />}
    </>
  );
}
